import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { UserPlus, Users, Trash2, Shield, Briefcase, User, Headphones, Building2, Search, X, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage, translations } from '@/contexts/LanguageContext.jsx';

export const UserManagementPage = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [supportAdmins, setSupportAdmins] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'client_admin',
    department: ''
  });
  const [supportFormData, setSupportFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    loadUsers();
    loadDepartments();
    if (user?.role === 'super_admin') {
      loadSupportAdmins();
    }
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(u => 
        u.name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query) ||
        u.department?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
    // Clear selection when search changes
    setSelectedUserIds([]);
  }, [searchQuery, users]);

  const loadDepartments = async () => {
    try {
      const response = await axios.get(`${API}/company/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error loading departments:', error);
      // Fallback departments
      setDepartments(['HR', 'Finance', 'Legal', 'Engineering', 'Operations', 'Marketing', 'Sales', 'IT', 'Admin']);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadSupportAdmins = async () => {
    try {
      const response = await axios.get(`${API}/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const admins = response.data.filter(u => u.role === 'support_admin');
      setSupportAdmins(admins);
    } catch (error) {
      console.error('Error loading support admins:', error);
    }
  };

  // Toggle single user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Toggle select all (excludes current user)
  const toggleSelectAll = () => {
    const selectableUsers = filteredUsers.filter(u => u.id !== user.id);
    if (selectedUserIds.length === selectableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(selectableUsers.map(u => u.id));
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    
    const confirmMessage = `Are you sure you want to delete ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    setIsDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const userId of selectedUserIds) {
      try {
        await axios.delete(`${API}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        successCount++;
      } catch (error) {
        console.error(`Error deleting user ${userId}:`, error);
        failCount++;
      }
    }
    
    setIsDeleting(false);
    setSelectedUserIds([]);
    
    if (successCount > 0) {
      toast.success(`${successCount} user${successCount > 1 ? 's' : ''} deleted successfully`);
      loadUsers();
      loadSupportAdmins();
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} user${failCount > 1 ? 's' : ''}`);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/users/create-admin`, {
        ...formData,
        client_id: user.client_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Invitation sent! User will receive an email to set up their account.');
      setDialogOpen(false);
      loadUsers();
      setFormData({
        email: '',
        name: '',
        role: 'client_admin',
        department: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupportAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/users/create-support-admin`, supportFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Support Admin created successfully!');
      setSupportDialogOpen(false);
      loadSupportAdmins();
      setSupportFormData({ email: '', password: '', name: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create support admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      loadUsers();
      loadSupportAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Shield size={20} className="text-red-400" />;
      case 'support_admin': return <Headphones size={20} className="text-pink-400" />;
      case 'client_admin': return <Briefcase size={20} className="text-indigo-400" />;
      case 'client_user': return <User size={20} className="text-slate-400" />;
      default: return <User size={20} />;
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'support_admin': return 'Support Admin';
      case 'client_admin': return 'Client Admin';
      case 'client_user': return 'Employee';
      default: return role;
    }
  };

  const canCreateUsers = user?.role === 'super_admin' || (user?.role === 'client_admin' && !user?.department);

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">{t.userManagement}</h1>
          <p className="text-base text-slate-400 mt-2">{t.manageTeamMembers}</p>
        </div>

        {canCreateUsers && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-user-button"
                className="mt-4 md:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md glow-effect"
              >
                <UserPlus size={18} className="mr-2" />
                {t.inviteUser}
              </Button>
            </DialogTrigger>
            
            {/* Bulk Upload Button */}
            <Button
              variant="outline"
              onClick={() => navigate('/suite/docs/bulk-upload')}
              className="mt-4 md:mt-0 ml-2 border-slate-700 hover:bg-slate-800"
              data-testid="bulk-upload-button"
            >
              <Upload size={18} className="mr-2" />
              {t.bulkUpload}
            </Button>
            
            <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-2xl font-semibold text-white">{t.inviteNewUser}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  {t.sendInvitation}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-slate-300">{t.emailAddress} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                    placeholder="user@company.com"
                    className="bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-slate-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-semibold text-slate-300">{t.name}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="name-input"
                    placeholder="John Doe"
                    className="bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-slate-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role" className="text-sm font-semibold text-slate-300">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 focus:border-indigo-500 text-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="client_admin" className="text-slate-200">Client Admin (Department Access)</SelectItem>
                      <SelectItem value="client_user" className="text-slate-200">Employee (Chat Only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.role === 'client_admin' && (
                  <div className="space-y-2">
                    <Label htmlFor="department" className="text-sm font-semibold text-slate-300">Department</Label>
                    <Select 
                      value={formData.department} 
                      onValueChange={(value) => setFormData({ ...formData, department: value === 'all_access' ? '' : value })}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 focus:border-indigo-500 text-slate-200">
                        <SelectValue placeholder="Select department..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                        <SelectItem value="all_access" className="text-emerald-400 font-medium">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            All Departments (Full Access)
                          </div>
                        </SelectItem>
                        <div className="border-t border-slate-700 my-1" />
                        {departments.map((dept) => (
                          <SelectItem key={dept} value={dept} className="text-slate-200">
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Select All Departments for full access, or choose a specific department</p>
                  </div>
                )}

                <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
                  <p className="text-xs text-indigo-300">
                    📧 An invitation email will be sent to the user with a link to set their password and activate their account.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="create-user-submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md glow-effect"
                >
                  {loading ? 'Sending Invite...' : 'Send Invitation'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!canCreateUsers && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm text-amber-400">
            ℹ️ As a department admin, you cannot create users. Contact your general admin or super admin.
          </p>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <Search size={18} className="text-slate-400" />
          <Input
            placeholder="Search by name, email, or department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="user-search-input"
            className="w-full max-w-md bg-slate-900 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-slate-200"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery('')}
              className="text-slate-400 hover:text-white"
            >
              <X size={16} />
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-500">
          {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Bulk Delete Bar - Shows when users are selected */}
      {selectedUserIds.length > 0 && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center justify-between animate-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={selectedUserIds.length === filteredUsers.filter(u => u.id !== user.id).length}
              onCheckedChange={toggleSelectAll}
              className="border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
            />
            <span className="text-red-300 font-medium">
              {selectedUserIds.length} user{selectedUserIds.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedUserIds([])}
              className="text-slate-400 hover:text-white"
            >
              <X size={16} className="mr-1" />
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bulk-delete-users-button"
            >
              {isDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete {selectedUserIds.length} User{selectedUserIds.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Select All Row - Shows when no users selected but users exist */}
      {canCreateUsers && selectedUserIds.length === 0 && filteredUsers.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <Checkbox
            checked={false}
            onCheckedChange={toggleSelectAll}
            className="border-slate-600"
          />
          <span className="text-slate-500 text-sm">Select all ({filteredUsers.filter(u => u.id !== user.id).length})</span>
        </div>
      )}

      {/* Users list */}
      <div className="space-y-3">
        {filteredUsers.map((userData) => (
          <Card
            key={userData.id}
            data-testid="user-card"
            className={`p-4 hover:bg-slate-800/50 transition-colors bg-slate-900/50 border-slate-800 ${
              selectedUserIds.includes(userData.id) ? 'ring-2 ring-cyan-500 bg-cyan-500/5' : ''
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Checkbox for selection - only show if can delete and not current user */}
              {canCreateUsers && userData.id !== user.id && (
                <Checkbox
                  checked={selectedUserIds.includes(userData.id)}
                  onCheckedChange={() => toggleUserSelection(userData.id)}
                  className="border-slate-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500 flex-shrink-0"
                />
              )}
              <div className="w-12 h-12 bg-indigo-600/10 rounded-full flex items-center justify-center border border-indigo-500/20">
                {getRoleIcon(userData.role)}
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-white">{userData.name}</h3>
                    <p className="text-sm text-slate-400">{userData.email}</p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Role</p>
                      <p className="text-sm text-slate-300 font-medium">{getRoleName(userData.role)}</p>
                    </div>
                    {userData.department && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Department</p>
                        <p className="text-sm text-slate-300 font-medium">{userData.department}</p>
                      </div>
                    )}
                    {!userData.department && userData.role === 'client_admin' && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Access</p>
                        <p className="text-sm text-indigo-400 font-medium">All Departments</p>
                      </div>
                    )}
                    {canCreateUsers && userData.id !== user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(userData.id)}
                        data-testid="delete-user-button"
                        className="text-slate-400 hover:text-red-400 hover:bg-red-950/50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {filteredUsers.length === 0 && users.length > 0 && (
          <div className="text-center py-16" data-testid="no-search-results">
            <Search size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No matching users</h3>
            <p className="text-slate-400">Try adjusting your search query</p>
            <Button
              variant="ghost"
              onClick={() => setSearchQuery('')}
              className="mt-4 text-indigo-400 hover:text-indigo-300"
            >
              Clear search
            </Button>
          </div>
        )}
        {users.length === 0 && (
          <div className="text-center py-16" data-testid="no-users">
            <Users size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No users found</h3>
            <p className="text-slate-400">Create your first user to get started</p>
          </div>
        )}
      </div>

      {/* Support Admin Management - Super Admin Only */}
      {user?.role === 'super_admin' && (
        <div className="mt-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                <Headphones className="text-pink-400" size={24} />
                Support Admin Management
              </h2>
              <p className="text-sm text-slate-400 mt-1">Manage support team who handle customer enquiries</p>
            </div>

            <Dialog open={supportDialogOpen} onOpenChange={setSupportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-support-admin-button"
                  className="mt-4 md:mt-0 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-md"
                >
                  <UserPlus size={18} className="mr-2" />
                  Create Support Admin
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-semibold text-white flex items-center gap-2">
                    <Headphones className="text-pink-400" size={24} />
                    Create Support Admin
                  </DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Support admins handle all customer enquiries, demo bookings, and chat requests
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSupportAdmin} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="support-name" className="text-sm font-semibold text-slate-300">Full Name</Label>
                    <Input
                      id="support-name"
                      value={supportFormData.name}
                      onChange={(e) => setSupportFormData({ ...supportFormData, name: e.target.value })}
                      required
                      data-testid="support-name-input"
                      placeholder="Support Team Member"
                      className="bg-slate-950 border-slate-800 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 text-slate-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="support-email" className="text-sm font-semibold text-slate-300">Email</Label>
                    <Input
                      id="support-email"
                      type="email"
                      value={supportFormData.email}
                      onChange={(e) => setSupportFormData({ ...supportFormData, email: e.target.value })}
                      required
                      data-testid="support-email-input"
                      placeholder="support@mydocorbit.com"
                      className="bg-slate-950 border-slate-800 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 text-slate-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="support-password" className="text-sm font-semibold text-slate-300">Password</Label>
                    <Input
                      id="support-password"
                      type="password"
                      value={supportFormData.password}
                      onChange={(e) => setSupportFormData({ ...supportFormData, password: e.target.value })}
                      required
                      data-testid="support-password-input"
                      placeholder="Minimum 6 characters"
                      className="bg-slate-950 border-slate-800 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 text-slate-200"
                    />
                  </div>

                  <div className="p-3 bg-pink-500/10 border border-pink-500/20 rounded-lg">
                    <p className="text-xs text-pink-300">
                      <strong>Support Admin Access:</strong> Can view and respond to all customer chats, manage demo bookings, and handle support notifications.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="create-support-submit"
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-md"
                  >
                    {loading ? 'Creating...' : 'Create Support Admin'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Support Admins List */}
          <div className="space-y-3">
            {supportAdmins.map((admin) => (
              <Card
                key={admin.id}
                data-testid="support-admin-card"
                className="p-4 hover:bg-slate-800/50 transition-colors bg-slate-900/50 border-slate-800 border-l-4 border-l-pink-500"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-pink-600/10 rounded-full flex items-center justify-center border border-pink-500/20">
                    <Headphones size={20} className="text-pink-400" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-white">{admin.name}</h3>
                        <p className="text-sm text-slate-400">{admin.email}</p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Role</p>
                          <p className="text-sm text-pink-400 font-medium">Support Admin</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Department</p>
                          <p className="text-sm text-slate-300 font-medium">Support</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(admin.id)}
                          data-testid="delete-support-button"
                          className="text-slate-400 hover:text-red-400 hover:bg-red-950/50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            {supportAdmins.length === 0 && (
              <div className="text-center py-8 border border-dashed border-slate-700 rounded-lg">
                <Headphones size={36} className="mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400 text-sm">No support admins yet</p>
                <p className="text-slate-500 text-xs mt-1">Create a support admin to handle customer enquiries</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
